import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartProvider';
import styles from './style.module.scss';
import { 
  MdShoppingCart, 
  MdAttachMoney, 
  MdDelete,
  MdAdd,
  MdRemove 
} from 'react-icons/md';
import { LuTicket } from 'react-icons/lu';
import { PiTimerFill } from 'react-icons/pi';
import { FiArrowRight, FiShoppingBag } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const StepCarrinho = ({ items, handleNextStep }) => {
  const { addToCart, removeFromCart, removeItemFromCart } = useCart();
  const navigate = useNavigate();

  const handleAddItem = (item) => {
    addToCart({ ...item, quantidade: 1 });
  };

  const handleRemoveItem = (itemId) => {
    const itemToRemove = items.find((item) => item.id === itemId);
    if (itemToRemove) {
      if (itemToRemove.quantidade > 1) {
        removeItemFromCart(itemId, 1);
      } else {
        removeFromCart(itemId);
      }
    }
  };

  const handleRemoveItemComplete = (itemId) => {
    removeFromCart(itemId);
  };

  function formatCurrency(value, locale = 'pt-BR', currency = 'BRL') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  }

  // Função para calcular os totais por grupo
  const totals = items.reduce((acc, item) => {
    if (!acc[item.nome]) acc[item.nome] = 0;
    acc[item.nome] += item.quantidade * item.valor;
    return acc;
  }, {});

  // Total geral
  const grandTotal = Object.values(totals).reduce((acc, value) => acc + value, 0);

  return (
    <div className={styles.container}>
      {/* Items List */}
      <div className={styles.itemsList}>
        {items.map((item) => (
          <div key={item.id} className={styles.cartItem}>
            <div className={styles.itemHeader}>
              <div className={styles.itemTitle}>
                <h3>{item.nome}</h3>
                <span className={styles.itemGroup}>Grupo {item.grupo}</span>
              </div>
              <BolaoCounter addedAt={item.addedAt} />
            </div>

            <div className={styles.itemContent}>
              <div className={styles.itemInfo}>
                <div className={styles.infoItem}>
                  <LuTicket className={styles.infoIcon} />
                  <div className={styles.infoText}>
                    <span className={styles.infoLabel}>Grupo</span>
                    <span className={styles.infoValue}>{item.grupo}</span>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <MdAttachMoney className={styles.infoIcon} />
                  <div className={styles.infoText}>
                    <span className={styles.infoLabel}>Valor Unitário</span>
                    <span className={styles.infoValue}>{formatCurrency(item.valor)}</span>
                  </div>
                </div>
              </div>

              <div className={styles.itemActions}>
                <div className={styles.quantityControl}>
                  <button 
                    className={styles.quantityButton}
                    onClick={() => handleRemoveItem(item.id)}
                    disabled={item.quantidade <= 1}
                  >
                    <MdRemove />
                  </button>
                  <span className={styles.quantity}>{item.quantidade}</span>
                  <button 
                    className={styles.quantityButton}
                    onClick={() => handleAddItem(item)}
                  >
                    <MdAdd />
                  </button>
                </div>

                <div className={styles.itemTotal}>
                  <span className={styles.totalLabel}>Total</span>
                  <span className={styles.totalValue}>
                    {formatCurrency(item.quantidade * item.valor)}
                  </span>
                </div>

                <button 
                  className={styles.removeButton}
                  onClick={() => handleRemoveItemComplete(item.id)}
                >
                  <MdDelete />
                  <span>Remover</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Summary */}
      <div className={styles.orderSummary}>
        <div className={styles.summaryHeader}>
          <h2>Resumo do Pedido</h2>
        </div>

        <div className={styles.summaryContent}>
          {Object.keys(totals).map((nome) => (
            <div key={nome} className={styles.summaryItem}>
              <span className={styles.summaryLabel}>{nome}</span>
              <span className={styles.summaryValue}>{formatCurrency(totals[nome])}</span>
            </div>
          ))}

          <div className={styles.summaryDivider}></div>

          <div className={styles.summaryTotal}>
            <span className={styles.totalLabel}>Total Geral</span>
            <span className={styles.totalValue}>{formatCurrency(grandTotal)}</span>
          </div>
        </div>

        <div className={styles.summaryActions}>
          <button 
            className={styles.nextButton}
            onClick={handleNextStep}
          >
            <span>Próximo Passo</span>
            <FiArrowRight />
          </button>
          
          <button 
            className={styles.continueButton}
            onClick={() => navigate('/')}
          >
            <FiShoppingBag />
            <span>Continuar Comprando</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const BolaoCounter = ({ addedAt }) => {
  const countdownDuration = 15 * 60 * 1000; // 15 minutos em milissegundos
  const [timeLeft, setTimeLeft] = useState(() => {
    const now = Date.now();
    const elapsed = now - addedAt;
    return countdownDuration - elapsed;
  });

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1000), 1000);
      return () => clearTimeout(timerId);
    }
  }, [timeLeft]);

  const minutes = Math.max(0, Math.floor(timeLeft / 60000));
  const seconds = Math.max(0, Math.floor((timeLeft % 60000) / 1000));
  const isUrgent = timeLeft < 5 * 60 * 1000; // Últimos 5 minutos

  return (
    <div className={`${styles.timer} ${isUrgent ? styles.timerUrgent : ''}`}>
      <PiTimerFill className={styles.timerIcon} />
      <span className={styles.timerText}>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
};

export default StepCarrinho;
